import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  IoCheckmarkCircle,
  IoClose,
  IoInformationCircle,
  IoWarning,
} from "react-icons/io5";

const NotificationContext = createContext(null);

let idSeq = 0;

function Toast({ item, onDismiss }) {
  const { id, type, message } = item;
  const styles = {
    success: {
      border: "border-vucali-primary/40",
      icon: <IoCheckmarkCircle className="h-6 w-6 shrink-0 text-vucali-primary" />,
      glow: "shadow-[0_0_24px_rgba(2,223,130,0.12)]",
    },
    error: {
      border: "border-red-500/40",
      icon: <IoWarning className="h-6 w-6 shrink-0 text-red-400" />,
      glow: "shadow-[0_0_24px_rgba(239,68,68,0.12)]",
    },
    info: {
      border: "border-cyan-500/35",
      icon: (
        <IoInformationCircle className="h-6 w-6 shrink-0 text-cyan-400" />
      ),
      glow: "shadow-[0_0_24px_rgba(34,211,238,0.1)]",
    },
  };
  const s = styles[type] || styles.info;

  return (
    <div
      role="status"
      className={`pointer-events-auto flex max-w-md items-start gap-3 rounded-xl border bg-vucali-surface/95 px-4 py-3 text-sm text-white backdrop-blur-md ${s.border} ${s.glow} animate-[slideIn_0.35s_ease-out]`}
    >
      {s.icon}
      <p className="min-w-0 flex-1 pt-0.5 leading-relaxed text-white/90">
        {message}
      </p>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        className="rounded-lg p-1 text-white/50 transition hover:bg-white/10 hover:text-white"
        aria-label="Dismiss"
      >
        <IoClose className="h-5 w-5" />
      </button>
    </div>
  );
}

export function NotificationProvider({ children }) {
  const [items, setItems] = useState([]);

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((type, message, duration = 4800) => {
    const id = ++idSeq;
    const item = { id, type, message };
    setItems((prev) => [...prev, item]);
    if (duration > 0) {
      window.setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const value = useMemo(
    () => ({
      notify: {
        success: (msg, d) => push("success", msg, d),
        error: (msg, d) => push("error", msg, d),
        info: (msg, d) => push("info", msg, d),
      },
    }),
    [push]
  );

  return (
    <NotificationContext.Provider value={value}>
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      {children}
      <div
        className="pointer-events-none fixed right-4 top-4 z-[9999] flex w-[min(100%-2rem,24rem)] flex-col gap-2"
        aria-live="polite"
      >
        {items.map((item) => (
          <Toast key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotify() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotify must be used within NotificationProvider");
  }
  return ctx.notify;
}
